import _ from "lodash";
import Link from "./link";
import DijkstraSearch from "./dijkstraSearch";
import { getMinProperty, getMaxProperty } from "../utils";
import Node from "./node";
import { LinkString, NodeLink, NodePath, NodeType } from "../types";
import { distanceType } from "./types";

export default class Graph {
  nodes: { [key: string]: Node };
  constructor() {
    this.nodes = {};
  }

  get linkArray(): NodeLink[] {
    return this.nodeArray.reduce<NodeLink[]>((links, node) => {
      const nodeLinks = node.paths.reduce<NodeLink[]>((nls, link) => {
        const end = this.nodes[link.toNodeName];
        // if (!end) return nls;
        return [
          ...nls,
          {
            start: node,
            length: link.length,
            end,
          },
        ];
      }, []);
      return [...links, ...nodeLinks];
    }, []);
  }

  get nodeArray(): Node[] {
    return Object.values(this.nodes);
  }

  get minX(): number {
    return getMinProperty<Node, number>(this.nodeArray, "x");
  }

  get minY(): number {
    return getMinProperty<Node, number>(this.nodeArray, "y");
  }

  get maxX(): number {
    return getMaxProperty<Node, number>(this.nodeArray, "x");
  }

  get maxY(): number {
    return getMaxProperty<Node, number>(this.nodeArray, "y");
  }

  search(start: string, end: string, distanceType: distanceType): NodePath {
    const path = DijkstraSearch.search(start, end, this, distanceType);
    return {
      start,
      end,
      path,
    };
  }

  copy(): Graph {
    return _.cloneDeep(this);
  }

  insertNode(node: Node): void {
    this.nodes = {
      ...this.nodes,
      [node.name]: node,
    };
  }

  removeNode(node: Node): void {
    const nodes = this.nodeArray;
    for (let i = 0; i < nodes.length; i += 1) {
      const nd = nodes[i];
      nd.paths = nd.paths.filter((path) => path.toNodeName !== node.name);
    }

    delete this.nodes[node.name];
  }

  createPath(from: Node, length: number, to: Node): void {
    if (!this.nodes[from.name] || !this.nodes[to.name]) {
      console.error("Nodes are not part of the graph!");
      return;
    }

    const path = new Link({ length, to: to.name });
    this.nodes[from.name].insertPath(path);
  }

  createTwoWayPath(from: Node, length: number, to: Node): void {
    this.createPath(from, length, to);
    this.createPath(to, length, from);
  }

  changeNodeName(node: Node, newName: string): void {
    const { name } = node;
    const nodes = this.nodeArray;

    for (let i = 0; i < nodes.length; i += 1) {
      const nd = nodes[i];

      for (let j = 0; j < nd.paths.length; j += 1) {
        const path = nd.paths[j];

        if (path.toNodeName === name) {
          path.toNodeName = newName;
        }
      }
    }

    this.nodes[newName] = this.nodes[name];
    this.nodes[newName].name = newName;
    delete this.nodes[name];
  }

  toJSON(): { nodes: NodeType[]; links: LinkString[] } {
    const nodes = this.nodeArray.map((node) => node.toJSON());
    const links: LinkString[] = this.linkArray.map((link) => ({
      start: link.start.name,
      length: link.length,
      end: link.end.name,
    }));
    return {
      nodes,
      links,
    };
  }

  static fromJSON(options: { nodes: NodeType[]; links: LinkString[] }) {
    const graph = new Graph();

    options.nodes.forEach((nd) => {
      const { name } = nd;

      const weight = Number(nd.weight);
      const x = Number(nd.x);
      const y = Number(nd.y);

      if (!name || isNaN(weight) || isNaN(x) || isNaN(y)) {
        console.warn("Invalid node, skipped.", nd);
        return;
      }
      const node = new Node({
        name,
        weight,
        x,
        y,
      });
      graph.insertNode(node);
    });

    options.links.forEach((link) => {
      const { start, end } = link;
      const length = Number(link.length);

      if (!start || !end || isNaN(length)) {
        console.warn("Invalid link, skipped.", link);
        return;
      }

      const from = graph.nodes[start];
      const to = graph.nodes[end];
      if (!from || !to) {
        console.warn("Invalid link, skipped.", link);
        return;
      }

      graph.createPath(from, length, to);
    });

    return graph;
  }
}
